import classNames from "classnames";
import React from "react";
import { Link } from "react-router-dom";

import { LottieLoading } from "../../components/graphics/LottieLoading";
import {
  useGetCalendlyQuery,
  useGetDemosQuery,
} from "../../slices/SupportSlice";
import { HelpDeskHeader } from "./HelpDeskParts";
import { demoStateLine, PILL_CLASSES } from "./support.utils";
import { PAGE, PAGE_INNER } from "./supportUi";

const when = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";

/**
 * Help desk › Demos (owner 2026-10-26): every demo booked in Calendly becomes a client + a "Demo – …" work item in
 * the owner's firm (status "Demo booked"). After the demo the owner pastes the script in the work item's Notes and
 * sets "Demo completed"; days later an AI follow-up draft written from those notes waits in Support cases.
 */
export const DemosPage = () => {
  const { data, isLoading, isError } = useGetDemosQuery(undefined, {
    pollingInterval: 300000,
  });
  const { data: calendly } = useGetCalendlyQuery();
  const demos = data?.demos || [];
  return (
    <div className={PAGE}>
      <div className={PAGE_INNER}>
        <HelpDeskHeader
          section={"demos"}
          subtitle={
            "Demos booked in Calendly. Each one is a client and a “Demo –” work item in your firm. After the demo, paste your notes on the work item and set it to “Demo completed”: a follow-up written from your notes is ready for you to check 3 days later."
          }
        />
        {calendly && calendly.available && !calendly.connected && (
          <div
            className={
              "mb-4 rounded-[14px] border border-[#F5DEB0] bg-[#FFF9EE] px-4 py-3 text-[13.5px] text-[#7A5A12]"
            }
            data-testid={"calendly-not-connected"}>
            <b>Calendly isn&apos;t connected yet,</b> so new demos don&apos;t
            show up here.{" "}
            <Link
              to={"/support/settings"}
              className={"font-semibold text-[#6B4EFF]"}>
              Connect Calendly ›
            </Link>
          </div>
        )}
        {data?.houseProblem && (
          <div
            className={
              "mb-4 rounded-[14px] border border-[#F5DEB0] bg-[#FFF9EE] px-4 py-3 text-[13.5px] text-[#7A5A12]"
            }>
            <b>Demos aren&apos;t added to your clients:</b> {data.houseProblem}
          </div>
        )}
        <div
          className={
            "overflow-hidden rounded-[14px] border border-[#EDEFF3] bg-white"
          }>
          <div
            className={
              "hidden grid-cols-[1.6fr_150px_1.4fr_1.2fr] gap-3.5 px-[18px] py-2.5 text-[12px] font-bold uppercase tracking-[.5px] text-[#5D6475] lg:grid"
            }>
            <span>Who</span>
            <span>Demo</span>
            <span>Where it stands</span>
            <span>Open</span>
          </div>
          {isError ? (
            <div className={"px-4 py-10 text-center text-sm text-[#8A90A2]"}>
              The demos couldn&apos;t be loaded. Please refresh the page.
            </div>
          ) : isLoading ? (
            <div className={"py-8"}>
              <LottieLoading />
            </div>
          ) : !data?.available ? (
            <div className={"px-4 py-10 text-center text-sm text-[#8A90A2]"}>
              Demos need a database update first.
            </div>
          ) : demos.length === 0 ? (
            <div className={"px-4 py-12 text-center"}>
              <div className={"text-[15px] font-semibold text-[#1D2C3B]"}>
                No demos yet
              </div>
              <div className={"mt-1 text-sm text-[#8A90A2]"}>
                Demos show here as soon as someone books one in Calendly.
              </div>
            </div>
          ) : (
            demos.map((d) => {
              const line = demoStateLine(d);
              return (
                <div
                  key={d.id}
                  data-testid={"demo-row"}
                  className={classNames(
                    "grid grid-cols-1 gap-2 border-t border-[#EDEFF3] px-[18px] py-3.5 lg:grid-cols-[1.6fr_150px_1.4fr_1.2fr] lg:items-center lg:gap-3.5",
                    d.state === "draft" && "bg-[#FBFAFF]",
                  )}>
                  <div className={"min-w-0"}>
                    <div
                      className={
                        "truncate text-[14.5px] font-bold text-[#1F2433]"
                      }>
                      {d.name}
                      {d.company ? (
                        <span className={"font-semibold text-[#5D6475]"}>
                          {" "}
                          · {d.company}
                        </span>
                      ) : null}
                    </div>
                    <div className={"truncate text-[12.5px] text-[#5D6475]"}>
                      {d.email}
                    </div>
                  </div>
                  <div className={"text-[13.5px] font-semibold text-[#1F2433]"}>
                    {when(d.demoAt)}
                  </div>
                  <div className={"min-w-0"}>
                    {d.state === "draft" && d.ticketId ? (
                      <Link
                        to={`/support/${d.ticketId}`}
                        className={classNames(
                          "rounded-full px-2.5 py-0.5 text-[12px] font-bold hover:underline",
                          PILL_CLASSES[line.tone],
                        )}>
                        {line.text}
                      </Link>
                    ) : (
                      <span
                        className={classNames(
                          "rounded-full px-2.5 py-0.5 text-[12px] font-bold",
                          PILL_CLASSES[line.tone],
                        )}>
                        {line.text}
                      </span>
                    )}
                    {d.problem && (
                      <div className={"mt-1 text-[12px] text-[#9A5B00]"}>
                        {d.problem}
                      </div>
                    )}
                  </div>
                  <div className={"flex flex-wrap gap-3 text-[13px]"}>
                    {d.workLink && (
                      <Link
                        to={d.workLink}
                        className={"font-semibold text-[#6B4EFF]"}>
                        Work item ›
                      </Link>
                    )}
                    {d.clientLink && (
                      <Link
                        to={d.clientLink}
                        className={"font-semibold text-[#6B4EFF]"}>
                        Client ›
                      </Link>
                    )}
                    {d.ticketId && d.state === "sent" && (
                      <Link
                        to={`/support/${d.ticketId}`}
                        className={"font-semibold text-[#6B4EFF]"}>
                        Follow-up ›
                      </Link>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
